'use client';

import { CompositeItem, useMergeRefs } from '@floating-ui/react';
import { IconChevronDownFilled } from '@zillow/constellation-icons/react/filled/icon-chevron-down-filled';
import { interactionResponse } from '@zillow/yield-callback';
import {
  Fragment,
  type MouseEventHandler,
  forwardRef,
  useCallback,
  useContext,
  useRef,
} from 'react';

import { css, cx } from '@/styled-system/css';

import { useDecoratedSlotContent } from '../../hooks/use-decorated-slot-content';
import { useInteractiveElementKeyDownCallback } from '../../hooks/use-interactive-element-key-down-callback';
import { expandIconRecipe } from '../../styles/expand-icon.styles';
import { Box } from '../box';
import { Icon } from '../icon';
import { Slot } from '../slot';
import { AccordionItemContext } from './context/accordion-item.context';
import { AccordionRootActionsContext } from './context/accordion-root.context';
import {
  accordionHeaderContentStyles,
  accordionHeaderRecipe,
} from './styles/accordion-header.styles';
import type { AccordionHeaderPropsInterface } from './types/accordion-header.types';

/**
 * Interactive heading text that hides or reveals the content in its sibling
 * `Accordion.Panel`.
 *
 * @since 8.43.0
 */

export const AccordionHeader = forwardRef<HTMLDivElement, AccordionHeaderPropsInterface>(
  (props: AccordionHeaderPropsInterface, forwardedRef) => {
    const { asChild = false, children, className, css: cssProp, onClick, ...rest } = props;

    const { disabled, expanded, value } = useContext(AccordionItemContext);
    const { toggleExpanded, shouldAwaitInteractionResponse } = useContext(
      AccordionRootActionsContext,
    );

    const styles = css(accordionHeaderRecipe.raw({ expanded, disabled }), cssProp);
    const iconStyles = css(expandIconRecipe.raw({ open: !!expanded }));

    const onAccordionHeaderClick: MouseEventHandler<HTMLDivElement> = useCallback(
      async (event) => {
        if (disabled) return;

        if (shouldAwaitInteractionResponse) {
          await interactionResponse();
        }

        toggleExpanded?.(value);
        onClick?.(event);
      },
      [disabled, onClick, value, toggleExpanded, shouldAwaitInteractionResponse],
    );

    const accordionHeaderRef = useRef<HTMLDivElement>(null);
    const refs = useMergeRefs([accordionHeaderRef, forwardedRef]);

    const onKeyDownCallback = useInteractiveElementKeyDownCallback({
      ref: accordionHeaderRef,
      onClick: onAccordionHeaderClick,
    });

    const Component = asChild ? Slot : 'div';

    const content = useDecoratedSlotContent({
      children,
      asChild,
      template: (childContent) => (
        <Fragment>
          <span className={cx('c11n', iconStyles)}>
            <Icon render={<IconChevronDownFilled />} />
          </span>
          <Box css={accordionHeaderContentStyles}>{childContent}</Box>
        </Fragment>
      ),
      dependencies: [iconStyles],
    });

    return (
      <CompositeItem
        render={() => (
          <Component
            role="button"
            ref={refs}
            id={`${value}-header`}
            onClick={onAccordionHeaderClick}
            onKeyDown={onKeyDownCallback}
            aria-expanded={expanded}
            aria-controls={`${value}-panel`}
            aria-disabled={disabled}
            data-c11n-component="Accordion.Header"
            data-c11n-version={__C11N_VERSION__}
            className={cx('c11n', styles, className)}
            tabIndex={0}
            {...rest}
          >
            {content}
          </Component>
        )}
      />
    );
  },
);

AccordionHeader.displayName = 'Accordion.Header';
